import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Check } from "lucide-react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { GET_SUBSCRIPTION_STATUS } from "../../graphql/queries";
import { summarizeSubscription } from "../../utils/subscription";
import {
  startTieredCheckout,
  cancelSubscription as cancelSubscriptionApi,
} from "../../utils/api/api";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./BillingPage.module.css";

// ── Checkout URLs ──────────────────────────────────────────────────────────────
const WEBAPP_BASE = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
const CHECKOUT_SUCCESS_URL = `${WEBAPP_BASE}/settings?checkout=success`;
const CHECKOUT_CANCEL_URL  = `${WEBAPP_BASE}/settings?checkout=cancel`;

// ── Display labels — frontend only, never sent to backend ─────────────────────
const PLAN_LABELS = {
  FREE:                 "Free",
  MONTHLY_SUBSCRIPTION: "Core",
  STARTER:              "Core",
  PRO:                  "Growth",
  PRO_PLUS:             "Scale",
};

// ── Plan definitions — keys MUST stay STARTER / PRO / PRO_PLUS ────────────────
const PLANS = [
  {
    key: "STARTER",
    eyebrow: "FOR SOLO MERCHANTS",
    name: "Core",
    tagline: "200 credits/month. Unlimited chat. Core features for getting started.",
    basePrice: 49,
    creditTiers: [
      { credits: 200, price: 49 },
      { credits: 300, price: 69 },
      { credits: 400, price: 89 },
    ],
    seatAddOn:  null,
    brandAddOn: null,
    cta: "Start Trial",
    featuresIntro: null,
    features: [
      "Unlimited Zeqi Chat",
      "1 brand account",
      "All store reports unlocked",
      "Basic business assessment",
      "Social media content creation (text and image)",
      "Up to 3 video generations",
      "Up to 10 TikTok slideshows",
      "Up to 10 blog articles",
      "Full SEO/GEO audit and research",
      "Brand knowledge base",
      "Content calendar",
      "Daily insights to your inbox",
      "Performance analytics dashboard",
    ],
  },
  {
    key: "PRO",
    eyebrow: "MOST POPULAR",
    name: "Growth",
    tagline: "700 credits/month. Full intelligence layer for growing brands.",
    basePrice: 119,
    creditTiers: [
      { credits: 700,  price: 119 },
      { credits: 900,  price: 129 },
      { credits: 1100, price: 159 },
    ],
    seatAddOn:  { pricePerSeat: 39, creditsPerSeat: 200, max: 10 },
    brandAddOn: null,
    cta: "Get Started",
    featuresIntro: "All features in Core",
    features: [
      "Full health-scored assessments",
      "Business Assessment Summary",
      "Strategy one-pager",
      "Task Agent (execution tracking)",
      "Meta/Google/TikTok ad analytics and optimization",
      "Email campaign execution",
      "Advanced SEO/GEO content writing",
      "Up to 30 video generations",
      "Up to 30 TikTok slideshows",
      "Up to 30 blog articles",
      "Team workspace (additional seats available)",
      "On-demand credit top-ups",
    ],
  },
  {
    key: "PRO_PLUS",
    eyebrow: "FOR TEAMS",
    name: "Scale",
    tagline: "2,000 credits/month. For power users and multi-brand teams.",
    basePrice: 199,
    creditTiers: [
      { credits: 2000, price: 199 },
      { credits: 3000, price: 299 },
      { credits: 4000, price: 399 },
    ],
    seatAddOn:  { pricePerSeat:  39, creditsPerSeat:  200, max: 10 },
    brandAddOn: { pricePerBrand: 39, creditsPerBrand: 200, max: 5  },
    cta: "Get Started",
    featuresIntro: "All features in Growth",
    features: [
      "Competitor intelligence, 14M+ live store data",
      "Up to 100 video generations",
      "Up to 100 TikTok slideshows",
      "Up to 100 blog articles",
      "Multi-brand management (additional brands available)",
      "Role-based access",
      "Priority report generation",
    ],
  },
];

const ENTERPRISE_FEATURES = [
  "All features in Scale",
  "Unlimited team members",
  "Unlimited brand accounts",
  "Dedicated success manager",
  "Custom AI trained on your brand voice",
  "1-hour priority SLA",
  "Custom integrations",
];

// ── Helper: compute total price ────────────────────────────────────────────────
function computeTotal(plan, creditTier, extraSeats, extraBrands) {
  const base      = creditTier?.price ?? plan.basePrice;
  const seatCost  = plan.seatAddOn  ? extraSeats  * plan.seatAddOn.pricePerSeat   : 0;
  const brandCost = plan.brandAddOn ? extraBrands * plan.brandAddOn.pricePerBrand : 0;
  return base + seatCost + brandCost;
}

// ── Plan Card ─────────────────────────────────────────────────────────────────
function PlanCard({ plan, subSummary, onSubscribe, loading }) {
  const isCurrentPlan =
    subSummary.isActive &&
    (subSummary.planFamily === plan.key ||
      (plan.key === "STARTER" && subSummary.planFamily === "MONTHLY_SUBSCRIPTION"));

  const isFeatured = plan.key === "PRO";

  const [selectedTier, setSelectedTier] = useState(plan.creditTiers[0]);
  const [extraSeats,   setExtraSeats]   = useState(0);
  const [extraBrands,  setExtraBrands]  = useState(0);

  const totalPrice = computeTotal(plan, selectedTier, extraSeats, extraBrands);

  const seatOptions  = plan.seatAddOn  ? Array.from({ length: plan.seatAddOn.max  + 1 }, (_, i) => i) : [];
  const brandOptions = plan.brandAddOn ? Array.from({ length: plan.brandAddOn.max + 1 }, (_, i) => i) : [];

  const handleSubscribe = () => {
    onSubscribe({
      planFamily: plan.key,   // STARTER | PRO | PRO_PLUS — never send the display name
      creditTier: selectedTier.credits,
      extraSeats,
      extraBrands,
    });
  };

  return (
    <div className={`${styles.planCard} ${isCurrentPlan ? styles.planCardCurrent : ""} ${isFeatured ? styles.planCardFeatured : ""}`}>
      {/* Header */}
      <div className={styles.planCardHeader}>
        <p className={styles.planEyebrow}>{plan.eyebrow}</p>
        <h3 className={styles.planName}>{plan.name}</h3>
        <p className={styles.planTagline}>{plan.tagline}</p>
      </div>

      {/* Pickers */}
      <div className={styles.pickerStack}>
        <div className={styles.addonBlock}>
          <label className={styles.addonLabel}>MONTHLY CREDITS</label>
          <div className={styles.selectWrap}>
            <select
              className={styles.addonSelect}
              value={selectedTier.credits}
              onChange={(e) => {
                const tier = plan.creditTiers.find((t) => t.credits === Number(e.target.value));
                if (tier) setSelectedTier(tier);
              }}
            >
              {plan.creditTiers.map((tier) => (
                <option key={tier.credits} value={tier.credits}>
                  {tier.credits.toLocaleString()} credits at ${tier.price}/mo
                </option>
              ))}
            </select>
          </div>
        </div>

        {plan.seatAddOn ? (
          <div className={styles.addonBlock}>
            <label className={styles.addonLabel}>ADDITIONAL TEAM SEATS</label>
            <div className={styles.selectWrap}>
              <select
                className={styles.addonSelect}
                value={extraSeats}
                onChange={(e) => setExtraSeats(Number(e.target.value))}
              >
                <option value={0}>No additional seats</option>
                {seatOptions.slice(1).map((n) => (
                  <option key={n} value={n}>
                    {n} extra seat{n > 1 ? "s" : ""} +${n * plan.seatAddOn.pricePerSeat}/mo
                    {" "}(+{n * plan.seatAddOn.creditsPerSeat} credits)
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {plan.brandAddOn ? (
          <div className={styles.addonBlock}>
            <label className={styles.addonLabel}>ADDITIONAL BRANDS</label>
            <div className={styles.selectWrap}>
              <select
                className={styles.addonSelect}
                value={extraBrands}
                onChange={(e) => setExtraBrands(Number(e.target.value))}
              >
                <option value={0}>No additional brands</option>
                {brandOptions.slice(1).map((n) => (
                  <option key={n} value={n}>
                    {n} brand{n > 1 ? "s" : ""} +${n * plan.brandAddOn.pricePerBrand}/mo
                    {" "}(+{n * plan.brandAddOn.creditsPerBrand} credits)
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
      </div>

      {/* Price */}
      <div className={styles.priceBlock}>
        <div className={styles.planPrice}>
          <span className={styles.planPriceCurrency}>$</span>
          <span className={styles.planPriceAmount}>{totalPrice}</span>
          <span className={styles.planPriceUnit}>/month</span>
        </div>
        <p className={styles.trialNote}>7-day free trial. Cancel anytime.</p>
      </div>

      {/* Features */}
      <ul className={styles.featureList}>
        {plan.featuresIntro ? (
          <li className={`${styles.featureItem} ${styles.featureItemIntro}`}>
            <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
            <span>{plan.featuresIntro}</span>
          </li>
        ) : null}
        {plan.features.map((feature) => (
          <li key={feature} className={styles.featureItem}>
            <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA — pinned to bottom */}
      <div className={styles.ctaWrap}>
        {isCurrentPlan ? (
          <button type="button" className={styles.planButtonCurrent} disabled>
            Current plan
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.planButton} ${isFeatured ? styles.planButtonFeatured : ""}`}
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? "Starting..." : `${plan.cta} →`}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Enterprise Card ────────────────────────────────────────────────────────────
function EnterpriseCard() {
  return (
    <div className={styles.planCard}>
      <div className={styles.planCardHeader}>
        <p className={styles.planEyebrow}>CUSTOM</p>
        <h3 className={styles.planName}>Enterprise</h3>
        <p className={styles.planTagline}>
          Full coverage, governance, and scale. Custom plans for large teams.
        </p>
      </div>

      <div className={styles.priceBlock}>
        <div className={styles.planPrice}>
          <span className={styles.planPriceAmount}>Custom</span>
        </div>
        <p className={styles.trialNote}>Dedicated onboarding.</p>
      </div>

      <ul className={styles.featureList}>
        {ENTERPRISE_FEATURES.map((f) => (
          <li key={f} className={styles.featureItem}>
            <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className={styles.ctaWrap}>
        <a
          href="mailto:hello@zeqi.co?subject=Enterprise Plan"
          className={styles.planButtonEnterprise}
        >
          Book a Demo →
        </a>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const BillingPage = () => {
  const { user } = useAuthUser();
  const authToken = user?.token;
  const userId    = user?.userId || user?._id || user?.id || null;

  const { data, loading: queryLoading, refetch } = useQuery(GET_SUBSCRIPTION_STATUS, {
    skip: !authToken,
    fetchPolicy: "cache-and-network",
    context: { headers: { Authorization: authToken ? `Bearer ${authToken}` : "" } },
  });

  const sub = data?.getSubscriptionStatus;
  const subSummary = useMemo(() => summarizeSubscription(sub), [sub]);

  const [subscribing, setSubscribing] = useState(false);
  const [cancelling,  setCancelling]  = useState(false);

  const handleSubscribe = useCallback(
    async ({ planFamily, creditTier, extraSeats, extraBrands }) => {
      if (!authToken || !userId) {
        window.alert("Please sign in before managing subscriptions.");
        return;
      }
      setSubscribing(true);
      try {
        const result = await startTieredCheckout({
          token: authToken,
          planFamily,       // STARTER | PRO | PRO_PLUS — backend enum, never the display name
          creditTier,
          extraSeats,
          extraBrands,
          successUrl: CHECKOUT_SUCCESS_URL,
          cancelUrl:  CHECKOUT_CANCEL_URL,
        });

        if (result?.checkoutUrl) {
          window.open(result.checkoutUrl, "_blank", "noopener,noreferrer");
          await refetch?.();
          window.alert(
            "Complete the payment in your browser. Your plan will update automatically once Stripe confirms the transaction."
          );
        } else if (result?.message) {
          window.alert(result.message);
        }
      } catch (e) {
        console.error("Checkout error", e?.message || e);
        window.alert(e?.message || "Unable to start checkout. Please try again.");
      } finally {
        setSubscribing(false);
      }
    },
    [authToken, userId, refetch]
  );

  const handleCancel = useCallback(async () => {
    if (!authToken || !userId) return;
    const confirmed = window.confirm(
      "Are you sure you want to cancel? You'll keep access until the end of your billing period."
    );
    if (!confirmed) return;
    setCancelling(true);
    try {
      await cancelSubscriptionApi({ token: authToken, userId });
      await refetch?.();
      window.alert("Subscription cancelled. Access continues until period end.");
    } catch (e) {
      window.alert(e?.message || "Couldn't cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  }, [authToken, userId, refetch]);

  const currentPlanName = PLAN_LABELS[subSummary.planFamily] || "Free";

  return (
    <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="billing">
      <header className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>Manage Your Subscription</h2>
        <p className={styles.pageSubtitle}>
          Current plan: <strong>{currentPlanName}</strong>
        </p>
      </header>

      {queryLoading ? (
        <p className={styles.loadingText}>Loading...</p>
      ) : (
        <div className={styles.planGrid}>
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              subSummary={subSummary}
              onSubscribe={handleSubscribe}
              loading={subscribing}
            />
          ))}
          <EnterpriseCard />
        </div>
      )}

      {subSummary.isActive && !subSummary.isFree ? (
        <div className={styles.dangerZone}>
          <button
            type="button"
            className={styles.manageButton}
            onClick={() => window.open("https://billing.stripe.com", "_blank")}
          >
            Manage billing details
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling..." : "Cancel subscription"}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default BillingPage;
